import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Stack,
  MenuItem,
  Skeleton,
  Paper,
  Button,
  Divider,
} from "@mui/material";
import Pagelayout from "../../components/Pagelayout";
import {
  addPayrollSettings,
  initializePayrollSettings,
  viewConfiguration,
} from "../../parollAPIs";
import { ChartOfAccounts, JournalTypes } from "../../payrollEnums";
import { useSelector } from "react-redux";
import {
  ArrowBack,
  Email,
  RefreshRounded,
  SupportAgent,
} from "@mui/icons-material";

const LabelTypography = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 600,
      color: "text.primary",
      marginBottom: 1,
    }}
  >
    {children}
  </Typography>
);

const PayrollSettings = () => {
  // Get data from redux store
  const { selectedJournal, selectedChartOfAccount, singleViewData } =
    useSelector((state) => state.payrollSlice);
  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [errors, setErrors] = useState({
    dayFrom: "",
    dayTo: "",
    salaryJournal: "",
    salaryPayables: "",
  });

  const [data, setData] = useState({
    dayFrom: "",
    dayTo: "",
    salaryJournal: "",
    salaryPayables: "",
  });

  // Create an array of days from 1 to 31

  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // Handle form field changes

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
    // Clear error when user enters a value
    setErrors({ ...errors, [name]: "" });
  };

  // Validate form fields

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!data.dayFrom) {
      newErrors.dayFrom = "Please select start day";
      isValid = false;
    }

    if (!data.dayTo) {
      newErrors.dayTo = "Please select end day";
      isValid = false;
    }

    if (!data.salaryJournal) {
      newErrors.salaryJournal = "Please select salary journal";
      isValid = false;
    }

    if (!data.salaryPayables) {
      newErrors.salaryPayables = "Please select salary payables account";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission  (Add or Update)

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaveBtnLoading(true);
    setSuccess(false);

    await addPayrollSettings({
      body: {
        salaryPeriodFrom: data.dayFrom,
        salaryPeriodTo: data.dayTo,
        salaryJournal: data.salaryJournal._id,
        salaryPayableAc: data.salaryPayables._id,
      },
      setSaveBtnLoading,
      setSuccess,
      setSnackbar,
    });
  };

  //  set data if singleViewData is available

  useEffect(() => {
    if (singleViewData && isLoading === false) {
      setData({
        dayFrom: singleViewData.salaryPeriodFrom,
        dayTo: singleViewData.salaryPeriodTo,
        salaryJournal: selectedJournal?.find(
          (res) => res?._id === singleViewData?.salaryJournal?._id
        ),
        salaryPayables: selectedChartOfAccount?.find(
          (res) => res._id === singleViewData?.salaryPayableAc?._id
        ),
      });
    }
  }, [singleViewData, isLoading]);

  // Initialize data

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      await initializePayrollSettings({
        journalTypes: [JournalTypes.INCOME],
        ChartOfAccounts: [ChartOfAccounts.PAYABLE],
        setIsLoading,
        setErrorMsg,
      });
    };

    initializeData();
    viewConfiguration()
  }, []);

  //Error handling

  const handleRetry = async () => {
    setIsLoading(true);
    setErrorMsg({ message: "" });
    await initializePayrollSettings({
      journalTypes: [JournalTypes.INCOME],
      ChartOfAccounts: [ChartOfAccounts.PAYABLE],
      setIsLoading,
      setErrorMsg,
    });
  };

  const handleBack = () => {
    // Navigate back or to a safe fallback page
    window.history.back();
  };

  return (
    <Pagelayout
      pageName="Payroll Settings"
      headerLayout={1}
      filter={false}
      pagination={false}
      searchBox={false}
      createBtn={false}
      saveBtn={errorMsg ==="" ? false : true}
      handleSave={handleSubmit}
      success={success}
      saveBtnLoading={saveBtnLoading}
      setSnackbar={setSnackbar}
      snackbar={snackbar}
    >
      {isLoading ? (
        <PayrollSettingsSkeleton />
      ) : errorMsg.message ? (
        <ErrorDisplay
          message={errorMsg.message}
          onRetry={handleRetry}
          onBack={handleBack}
        />
      ) : (
        <Box sx={{ margin: "0 auto" }}>
          <Stack spacing={3}>
            <LabelTypography>SALARY PERIOD</LabelTypography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                size="small"
                fullWidth
                select
                label="Day From"
                variant="outlined"
                name="dayFrom"
                value={data.dayFrom}
                onChange={handleChange}
                error={!!errors.dayFrom}
                helperText={errors.dayFrom}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 200, marginTop: 8 } },
                  },
                }}
                sx={{ bgcolor: "background.paper" }}
              >
                {days.map((day) => (
                  <MenuItem key={day} value={day}>
                    Day {day}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                fullWidth
                select
                label="Day To"
                variant="outlined"
                name="dayTo"
                value={data.dayTo}
                onChange={handleChange}
                error={!!errors.dayTo}
                helperText={errors.dayTo}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 200, marginTop: 8 } },
                  },
                }}
                sx={{ bgcolor: "background.paper" }}
              >
                {days.map((day) => (
                  <MenuItem key={day} value={day}>
                    Day {day}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <LabelTypography>SALARY JOURNAL</LabelTypography>
              <Autocomplete
                size="small"
                fullWidth
                getOptionLabel={(option) => option.journalName || ""}
                options={selectedJournal || []}
                value={data.salaryJournal}
                onChange={(event, newValue) => {
                  handleChange({
                    target: {
                      name: "salaryJournal",
                      value: newValue,
                    },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Salary Journal"
                    variant="outlined"
                    error={!!errors.salaryJournal}
                    helperText={errors.salaryJournal}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Box>

            <Box>
              <LabelTypography>SALARY PAYABLES A/C</LabelTypography>
              <Autocomplete
                size="small"
                fullWidth
                getOptionLabel={(option) => option.accountName || ""}
                options={selectedChartOfAccount || []}
                value={data.salaryPayables}
                onChange={(event, newValue) => {
                  handleChange({
                    target: {
                      name: "salaryPayables",
                      value: newValue,
                    },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Salaries Payable A/C"
                    variant="outlined"
                    error={!!errors.salaryPayables}
                    helperText={errors.salaryPayables}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Pagelayout>
  );
};

export default PayrollSettings;


// Skeleton loader component
export const PayrollSettingsSkeleton = () => (
  <Box sx={{ margin: "0 auto" }}>
    <Stack spacing={3}>
      {/* Salary Period Section Skeleton */}
      <Skeleton width={150} height={32} /> {/* For "SALARY PERIOD" label */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Skeleton height={40} /> {/* For Day From field */}
        <Skeleton height={40} /> {/* For Day To field */}
      </Box>
      {/* Salary Journal Section Skeleton */}
      <Box>
        <Skeleton width={150} height={32} /> {/* For "SALARY JOURNAL" label */}
        <Skeleton height={40} /> {/* For Salary Journal autocomplete */}
      </Box>
      {/* Salary Payables Section Skeleton */}
      <Box>
        <Skeleton width={200} height={32} />{" "}
        {/* For "SALARY PAYABLES A/C" label */}
        <Skeleton height={40} /> {/* For Salary Payables autocomplete */}
      </Box>
    </Stack>
  </Box>
);

// Error display component
export const ErrorDisplay = ({ message, onRetry, onBack }) => {
  const handleSupportClick = () => {
    // You can replace this with your support email or link
    window.location.href = "mailto:support@leeyet.com";
  };

  const handleHelpClick = () => {
    // Replace with your help center URL
    window.open("https://help.yourcompany.com", "_blank");
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: "100%",
          textAlign: "center",
        }}
      >
        {/* Error Icon and Title */}
        <Stack spacing={3} alignItems="center">
          <Box
            component="img"
            src="https://img.freepik.com/free-vector/error-colored-isometric-composition-signs-tools-materials-solving-problems-errors-computer-vector-illustration_1284-67437.jpg?t=st=1736422001~exp=1736425601~hmac=9c4ce266e1299d0c21916d006fc9364c8b8e53c55041915f2c019862fb116bf7&w=826" // Replace with your error image
            alt="Error"
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              opacity: 0.8,
            }}
          />

          <Typography variant="h5" color="error" gutterBottom fontWeight="500">
            Something went wrong
          </Typography>

          {/* Error Message */}
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {message ||
              "We couldn't load the payroll settings. Please try again or contact support if the problem persists."}
          </Typography>

          <Divider sx={{ width: "100%" }} />

          {/* Primary Actions */}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<RefreshRounded />}
              onClick={onRetry}
              sx={{ minWidth: 120 }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{ minWidth: 120 }}
            >
              Go Back
            </Button>
          </Stack>

          {/* Help Options */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Need help?
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              {/* <Button
                size="small"
                startIcon={<SupportAgent />}
                onClick={handleHelpClick}
              >
                Help Center
              </Button> */}
              <Button
                size="small"
                startIcon={<Email />}
                onClick={handleSupportClick}
              >
                Contact Support
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};
